import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import {
    Search,
    TrendingUp,
    Users,
    User,
    X,
    Download,
    Bookmark,
    Phone,
    Mail,
    ClipboardList,
} from "lucide-react";
import "./SourcingModal.css";

// ─── nouveaux imports ────────────────────────────────────────────────────────
import HistoriqueModal from "./SourcingHistorique";
// ─────────────────────────────────────────────────────────────────────────────

import { fetchCandidatsRequest } from "../../../../redux/actions/candidatsActions";
import {
    fetchCandidaturesRequest,
    createCandidatureRequest,
} from "../../../../redux/actions/candidaturesActions";
import {
    fetchSavedCandidatesByDemandeRequest,
    saveCandidateForDemandeRequest,
    unsaveCandidateForDemandeRequest,
} from "../../../../redux/actions/demandeCandidatSauvegardeActions";
import { fetchOffreSalarialeByCandidatureRequest } from "../../../../redux/actions/offresSalarialesActions";
import { fetchEtablissementsRequest } from "../../../../redux/actions/etablissementsActions";
import {
    API_BASE_URL,
    extractErrorMessage,
    getBlobRequest,
    postRequest,
} from "../../../../../api";

const showSwal = (options) => {
    Swal.fire({
        ...options,
        customClass: {
            ...(options.customClass || {}),
            container: "swal-above-modal",
        },
        didOpen: () => {
            const container = document.querySelector(".swal-above-modal");
            if (container) container.style.zIndex = "99999";
            if (options.didOpen) options.didOpen();
        },
    });
};

const NIVEAU_ETUDES_LABELS = {
    DIPLOME_BAC_2: "Diplôme (Bac + 2)",
    LICENCE_BAC_3: "Licence (Bac + 3)",
    MASTER_BAC_5: "Master (Bac + 5)",
    INGENIEUR_BAC_5: "Ingénieur (Bac + 5)",
    DOCTORAT_ET_PLUS: "Doctorat et plus",
};

const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};
const FIXED_RECOMMENDATION_SCORES = [91, 76, 45];
const FIXED_RECOMMENDATIONS_STORAGE_KEY = "sourcing-fixed-ai-recommendations";

const compareCandidatIds = (first, second) => {
    const firstId = Number(first.id);
    const secondId = Number(second.id);
    if (Number.isFinite(firstId) && Number.isFinite(secondId)) return firstId - secondId;
    return String(first.id).localeCompare(String(second.id));
};

const MATCHING_REPORT_INSIGHTS = [
    {
        strengths: "Forte adéquation des compétences, de l'expérience et de la formation avec le poste.",
        vigilance: "Quelques compétences techniques spécifiques sont absentes du profil.",
    },
    {
        strengths: "Bonne correspondance entre le profil et les exigences du poste.",
        vigilance: "Expérience sur certains domaines du poste limitée..",
    },
    {
        strengths: "Formation pertinente et compétences de base en lien avec le poste.",
        vigilance: "Plusieurs compétences clés et expériences requises sont manquantes.",
    },
];

const TYPE_CANDIDATURE_OPTIONS = [
    { value: "REPONSE_OFFRE", label: "Par poste" },
    { value: "CANDIDATURE_SPONTANEE", label: "Candidature spontanée" },
    { value: "AUTRE", label: "Autre" },
];

const SOURCE_OPTIONS = [
    { value: "SITES_CARRIERE", label: "Sites carrière" },
    { value: "CHASSEURS_TETE", label: "Chasseurs de tête" },
    { value: "PARTENAIRES_ACADEMIQUES", label: "Partenaires académiques" },
    { value: "COOPTATION_RECOMMENDATION", label: "Cooptation / recommendation" },
    { value: "AUTRE", label: "Autre" },
];

const labelFromOptions = (options, value, fallback = "—") => {
    if (!value) return fallback;
    const found = options.find((o) => String(o.value) === String(value));
    return found?.label || value;
};

const normalizeCandidat = (c) => {
    const nom = c?.nomCandidat ?? c?.nom ?? "";
    const prenom = c?.prenomCandidat ?? c?.prenom ?? "";
    const email = c?.emailCandidat ?? c?.email ?? "";
    const phone = c?.phoneCandidat ?? c?.phone ?? c?.telephone ?? "";
    const niveauEtude = c?.niveauxEtudes ?? c?.niveauEtude ?? c?.niveauEtudes ?? "";
    const anneExperience = c?.anneExperience ?? c?.anneesExperience ?? "";
    const diplomeObtenu = c?.diplome ?? c?.diplomeObtenu ?? "";
    const etablissementId = c?.etablissementId ?? c?.etablissement?.id ?? null;
    const autreEtablissement = c?.autreEtablissement ?? "";
    const typeCandidature = c?.typeCandidature ?? "";
    const source = c?.source ?? "";
    const cv = c?.cvDocument ?? c?.cv ?? "";
    const dateAjout = c?.createdAt ?? c?.dateAjout ?? c?.createdDate ?? null;

    return {
        raw: c,
        id: c?.id,
        nomCandidat: nom,
        prenomCandidat: prenom,
        emailCandidat: email,
        phoneCandidat: phone,
        niveauEtude,
        anneExperience,
        diplomeObtenu,
        etablissementId,
        autreEtablissement,
        typeCandidature,
        source,
        cv,
        dateAjout,
    };
};

const labelNiveauEtude = (value) => {
    if (!value) return "—";
    return NIVEAU_ETUDES_LABELS[value] || value;
};

const EMPTY_IMPORT_RESULT = {
    totalUnreadEmails: 0,
    processedEmails: 0,
    importedCount: 0,
    ignoredCount: 0,
    files: [],
    errors: [],
};

const parseExp = (exp) => {
    if (!exp) return 0;
    const value = String(exp).toLowerCase();
    if (value.includes("< 1")) return 0;
    if (value.includes("> 10")) return 10;
    if (value.includes("> 5")) return 5;
    if (value.includes("> 2")) return 2;
    if (value.includes("> 1")) return 1;
    return 0;
};

// ─── Bouton icône historique ──────────────────────────────────────────────────

const HistoriqueBtn = ({ count, onClick }) => (
    <button
        type="button"
        onClick={(e) => {
            e.stopPropagation();
            onClick();
        }}
        title={`${count} candidature${count > 1 ? "s" : ""} dans l'historique`}
        style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            padding: "4px 8px",
            backgroundColor: count > 0 ? "#204385" : "#f3f4f6",
            color: count > 0 ? "#fff" : "#9ca3af",
            border: "none",
            borderRadius: 6,
            fontSize: 11,
            fontWeight: 600,
            cursor: count > 0 ? "pointer" : "default",
            transition: "opacity 0.15s",
            pointerEvents: count > 0 ? "auto" : "none",
            whiteSpace: "nowrap",
        }}
        onMouseOver={(e) => {
            if (count > 0) e.currentTarget.style.opacity = "0.8";
        }}
        onMouseOut={(e) => {
            e.currentTarget.style.opacity = "1";
        }}
    >
        <ClipboardList size={13} />
        {count > 0 ? count : "—"}
    </button>
);

// ─────────────────────────────────────────────────────────────────────────────

export default function SourcingModal({ open, onClose, demande }) {
    const dispatch = useDispatch();

    const candidatsState = useSelector((state) => state.candidats || {});
    const candidaturesState = useSelector((state) => state.candidatures || {});
    const etablissementsState = useSelector((state) => state.etablissements || {});
    const savedState = useSelector((state) => state.demandeCandidatSauvegarde || {});
    const offresSalarialesState = useSelector((state) => state.offresSalariales || {});

    const candidatsRaw =
        candidatsState.candidats || candidatsState.items || candidatsState.data || EMPTY_ARRAY;
    const etablissements = etablissementsState.etablissements || EMPTY_ARRAY;
    const candidaturesToutes = candidaturesState.candidatures || EMPTY_ARRAY;
    const savedCandidates = savedState.savedCandidates || EMPTY_ARRAY;
    const offreByCandidature = offresSalarialesState.offreByCandidature || EMPTY_OBJECT;

    const safeDemande = demande || {};
    const candidaturesDemande = useMemo(
        () =>
            (candidaturesToutes || []).filter(
                (candidature) => Number(candidature.demandeId) === Number(safeDemande?.id)
            ),
        [candidaturesToutes, safeDemande?.id]
    );

    const [selectedCandidates, setSelectedCandidates] = useState([]);
    const [fixedRecommendations, setFixedRecommendations] = useState({ key: "", ids: [] });
    const [filters, setFilters] = useState({
        fullname: "",
        source: "",
        typeCandidature: "",
        experience: "",
        diplomeObtenu: "",
        niveauEtude: "",
        sauvegarde: "",
    });
    const [importLoading, setImportLoading] = useState(false);
    const [mailImportResult, setMailImportResult] = useState(null);

    // ── état du modal historique ──────────────────────────────────────────────
    const [historiqueModal, setHistoriqueModal] = useState({
        open: false,
        candidat: null,
    });

    useEffect(() => {
        if (!open) return;

        dispatch(fetchCandidatsRequest());
        dispatch(fetchEtablissementsRequest());
        dispatch(fetchCandidaturesRequest());

        if (safeDemande?.id) {
            dispatch(fetchSavedCandidatesByDemandeRequest(safeDemande.id));
        }

        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = "unset";
        };
    }, [dispatch, open, safeDemande?.id]);

    useEffect(() => {
        if (!open) {
            setSelectedCandidates([]);
            setFilters({
                fullname: "",
                source: "",
                typeCandidature: "",
                experience: "",
                diplomeObtenu: "",
                niveauEtude: "",
                sauvegarde: ""
            });
            setImportLoading(false);
            setMailImportResult(null);
        }
    }, [open]);

    useEffect(() => {
        if (!open) return;

        (candidaturesToutes || []).forEach((candidature) => {
            if (candidature?.id && !offreByCandidature[candidature.id]) {
                dispatch(fetchOffreSalarialeByCandidatureRequest(candidature.id));
            }
        });
    }, [dispatch, open, candidaturesToutes, offreByCandidature]);

    const candidats = useMemo(() => {
        return Array.isArray(candidatsRaw) ? candidatsRaw.map(normalizeCandidat) : [];
    }, [candidatsRaw]);

    const recommendationsStorageKey = `${FIXED_RECOMMENDATIONS_STORAGE_KEY}:${
        sessionStorage.getItem("selectedCompanyId") || "default"
    }`;

    useEffect(() => {
        const availableCandidates = candidats
            .filter((candidat) => candidat.id != null && candidat.cv)
            .sort(compareCandidatIds);
        const availableById = new Map(
            availableCandidates.map((candidat) => [String(candidat.id), candidat])
        );
        if (!availableCandidates.length) return;

        setFixedRecommendations((previous) => {
            let preferredIds = previous.key === recommendationsStorageKey ? previous.ids : [];
            if (!preferredIds.length) {
                try {
                    const storedIds = JSON.parse(localStorage.getItem(recommendationsStorageKey) || "[]");
                    preferredIds = Array.isArray(storedIds) ? storedIds : [];
                } catch {
                    preferredIds = [];
                }
            }

            const usedIds = new Set();
            const slots = FIXED_RECOMMENDATION_SCORES.map((_, index) => {
                const preferredId = preferredIds[index];
                const candidat = preferredId != null ? availableById.get(String(preferredId)) : null;
                if (!candidat || usedIds.has(String(candidat.id))) return null;
                usedIds.add(String(candidat.id));
                return candidat;
            });

            availableCandidates.forEach((candidat) => {
                const candidatId = String(candidat.id);
                const emptySlotIndex = slots.findIndex((slot) => !slot);
                if (emptySlotIndex === -1 || usedIds.has(candidatId)) return;
                slots[emptySlotIndex] = candidat;
                usedIds.add(candidatId);
            });

            const nextIds = slots.map((candidat) => (candidat ? String(candidat.id) : null));
            try {
                localStorage.setItem(recommendationsStorageKey, JSON.stringify(nextIds));
            } catch {
                // La sélection reste stable pendant la session si le stockage est indisponible.
            }

            if (
                previous.key === recommendationsStorageKey &&
                JSON.stringify(previous.ids) === JSON.stringify(nextIds)
            ) {
                return previous;
            }
            return { key: recommendationsStorageKey, ids: nextIds };
        });
    }, [candidats, recommendationsStorageKey]);

    // Historique reel de toutes les candidatures rattachees au candidat.
    const getHistorique = (candidatId) => {
        return (candidaturesToutes || []).filter(
            (candidature) => Number(candidature.candidatId) === Number(candidatId)
        ).map((candidature) => ({
            ...candidature,
            offreSalariale: offreByCandidature[candidature.id] || candidature.offreSalariale || null,
            statutOffre:
                offreByCandidature[candidature.id]?.statutOffre ||
                candidature.offreSalariale?.statutOffre ||
                candidature.statutOffre ||
                "",
        }));
    };

    // ─────────────────────────────────────────────────────────────────────────

    const alreadyLinkedIds = useMemo(() => {
        return new Set(
            (candidaturesDemande || [])
                .map((c) => Number(c.candidatId))
                .filter((id) => !Number.isNaN(id))
        );
    }, [candidaturesDemande]);

    const savedIds = useMemo(() => {
        return new Set(
            (savedCandidates || [])
                .map((c) => Number(c.candidatId))
                .filter((id) => !Number.isNaN(id))
        );
    }, [savedCandidates]);

    const getEtablissementLabel = (candidat) => {
        if (candidat?.autreEtablissement?.trim()) return candidat.autreEtablissement.trim();

        const et = (etablissements || []).find(
            (e) => Number(e.id) === Number(candidat?.etablissementId)
        );

        return et ? et.abbreviation || et.nom || "—" : "—";
    };

    const getDemandePosteLabel = () => {
        if (safeDemande?.autrePoste?.trim()) return safeDemande.autrePoste.trim();
        return safeDemande?.posteNom || safeDemande?.poste || "—";
    };

    const getScoreColor = (score) => {
        if (score >= 85) return "#4CAF50";
        if (score >= 70) return "#2196F3";
        if (score >= 50) return "#ff9800";
        return "#f44336";
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "—";
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return "—";
        return d.toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return "-";
        const d = new Date(dateStr);
        if (Number.isNaN(d.getTime())) return "-";
        return d.toLocaleString("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const handleDownloadCV = async (candidat) => {
        try {
            if (!candidat?.cv) {
                showSwal({
                    icon: "warning",
                    text: "Aucun CV disponible pour ce candidat",
                    timer: 1800,
                    showConfirmButton: false,
                });
                return;
            }

            const token = sessionStorage.getItem("token");
            const uid = sessionStorage.getItem("uid");
            const selectedCompanyId = sessionStorage.getItem("selectedCompanyId");

            const url = `${API_BASE_URL}/api/candidat-theque/cv/${encodeURIComponent(candidat.cv)}`;

            const response = await getBlobRequest({
                url,
                token,
                extraHeaders: {
                    uid,
                    "X-Tenant-Id": selectedCompanyId,
                },
            });

            const blob = new Blob([response.data]);
            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = candidat.cv;
            document.body.appendChild(link);
            link.click();
            link.remove();

            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error("Erreur téléchargement CV:", error);
            showSwal({
                icon: "error",
                text: "Impossible de télécharger le CV.",
                timer: 2200,
                showConfirmButton: false,
            });
        }
    };
    const [lastSynchronization, setLastSynchronization] = useState(new Date());
    const formatSynchronizationDate = (date) => {
        if (!date) return "Jamais";

        return new Intl.DateTimeFormat("fr-FR", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        }).format(date);
    };
    const handleImportMailAttachments = async () => {
        setImportLoading(true);

        try {
            const token = sessionStorage.getItem("token");
            const response = await postRequest({
                url: `${API_BASE_URL}/api/sourcing/mail/import-unread-attachments`,
                data: {},
                token,
            });

            const result = response?.data || EMPTY_IMPORT_RESULT;

            setMailImportResult(result);

            if ((result.importedCount || 0) > 0) {
                showSwal({
                    icon: "success",
                    text: `Import termine : ${result.importedCount} piece(s) jointe(s) recuperee(s).`,
                    timer: 2200,
                    showConfirmButton: false,
                });
            } else {
                showSwal({
                    icon: "warning",
                    text: "Aucune piece jointe CV trouvee dans les emails non lus.",
                    timer: 2400,
                    showConfirmButton: false,
                });
            }
        } catch (error) {
            console.error("Erreur import mailbox sourcing:", error);
            showSwal({
                icon: "error",
                text: extractErrorMessage(
                    error,
                    "Impossible d'importer les pieces jointes depuis la boite mail."
                ),
            });
        } finally {
            setImportLoading(false);
        }
    };

    const aiRecommendations = useMemo(() => {
        const candidatsById = new Map(candidats.map((candidat) => [String(candidat.id), candidat]));
        return fixedRecommendations.ids
            .map((candidatId, index) => {
                const candidat = candidatId != null ? candidatsById.get(String(candidatId)) : null;
                return candidat
                    ? { ...candidat, matchScore: FIXED_RECOMMENDATION_SCORES[index] }
                    : null;
            })
            .filter(Boolean);
    }, [candidats, fixedRecommendations.ids]);

    const sourceOptions = useMemo(() => {
        const values = [
            ...new Set(
                candidats
                    .map((c) => labelFromOptions(SOURCE_OPTIONS, c.source, ""))
                    .filter(Boolean)
            ),
        ];
        return values.sort((a, b) => a.localeCompare(b));
    }, [candidats]);

    const niveauOptions = useMemo(() => {
        const values = [
            ...new Set(
                candidats.map((c) => labelNiveauEtude(c.niveauEtude)).filter(Boolean)
            ),
        ];
        return values.sort((a, b) => a.localeCompare(b));
    }, [candidats]);

    const typeCandidatureOptions = useMemo(() => {
        const values = [
            ...new Set(
                candidats
                    .map((c) => labelFromOptions(TYPE_CANDIDATURE_OPTIONS, c.typeCandidature, ""))
                    .filter(Boolean)
            ),
        ];
        return values.sort((a, b) => a.localeCompare(b));
    }, [candidats]);

    const experienceOptions = useMemo(() => {
        const values = [
            ...new Set(
                candidats
                    .map((c) => String(c.anneExperience || "").trim())
                    .filter(Boolean)
            ),
        ];
        return values.sort((a, b) => parseExp(a) - parseExp(b) || a.localeCompare(b));
    }, [candidats]);

    const filteredCandidats = useMemo(() => {
        return candidats.filter((c) => {
            if (filters.fullname) {
                const fullName = `${c.nomCandidat} ${c.prenomCandidat}`.toLowerCase();
                if (!fullName.includes(filters.fullname.toLowerCase())) return false;
            }

            if (
                filters.source &&
                labelFromOptions(SOURCE_OPTIONS, c.source, "") !== filters.source
            ) {
                return false;
            }

            if (
                filters.typeCandidature &&
                labelFromOptions(TYPE_CANDIDATURE_OPTIONS, c.typeCandidature, "") !== filters.typeCandidature
            ) {
                return false;
            }

            if (filters.experience && String(c.anneExperience || "").trim() !== filters.experience) {
                return false;
            }

            if (
                filters.diplomeObtenu &&
                !String(c.diplomeObtenu || "")
                    .toLowerCase()
                    .includes(filters.diplomeObtenu.toLowerCase())
            ) {
                return false;
            }

            if (
                filters.niveauEtude &&
                labelNiveauEtude(c.niveauEtude) !== filters.niveauEtude
            ) {
                return false;
            }

            if (filters.sauvegarde === "saved" && !savedIds.has(Number(c.id))) return false;
            if (filters.sauvegarde === "unsaved" && savedIds.has(Number(c.id))) return false;

            return true;
        });
    }, [candidats, filters]);

    const toggleSelection = (candidat) => {
        const candidateId = Number(candidat.id);
        if (alreadyLinkedIds.has(candidateId)) return;

        const isSelected = selectedCandidates.some((c) => Number(c.id) === candidateId);

        setSelectedCandidates((prev) =>
            isSelected
                ? prev.filter((c) => Number(c.id) !== candidateId)
                : [...prev, candidat]
        );
    };

    const toggleSaved = (candidat) => {
        if (!safeDemande?.id || !candidat?.id) return;

        const candidateId = Number(candidat.id);

        if (alreadyLinkedIds.has(candidateId)) {
            showSwal({
                icon: "info",
                text: "Ce candidat est déjà proposé à cette demande",
                timer: 1800,
                showConfirmButton: false,
            });
            return;
        }

        if (savedIds.has(candidateId)) {
            dispatch(unsaveCandidateForDemandeRequest(Number(safeDemande.id), candidateId));
        } else {
            dispatch(
                saveCandidateForDemandeRequest({
                    demandeId: Number(safeDemande.id),
                    candidatId: candidateId,
                })
            );
        }
    };

    const handleSave = () => {
        if (!safeDemande?.id) {
            showSwal({
                icon: "warning",
                text: "Aucune demande sélectionnée",
                timer: 1800,
                showConfirmButton: false,
            });
            return;
        }

        if (selectedCandidates.length === 0) {
            showSwal({
                icon: "warning",
                text: "Veuillez sélectionner au moins un candidat",
                timer: 1800,
                showConfirmButton: false,
            });
            return;
        }

        selectedCandidates.forEach((candidat) => {
            dispatch(
                createCandidatureRequest({
                    demandeId: Number(safeDemande.id),
                    candidatId: Number(candidat.id),
                    statut: "en attente",
                })
            );
        });

        showSwal({
            icon: "success",
            text: `${selectedCandidates.length} candidat(s) proposé(s) pour le poste "${getDemandePosteLabel()}"`,
            timer: 1800,
            showConfirmButton: false,
        });

        setSelectedCandidates([]);
        onClose();
    };

    if (!open) return null;

    return (
        <>
            {/* ── Modal Sourcing ── */}
            <div className="smModal" onMouseDown={onClose} role="dialog" aria-modal="true">
                <div className="smModal__panel" onMouseDown={(e) => e.stopPropagation()}>
                    <div className="smModal__header">
                        <div className="smModal__title">
                            <Users size={18} />
                            Sourcing des candidats — {getDemandePosteLabel()}
                        </div>
                        <button
                            className="smIconBtn"
                            onClick={onClose}
                            aria-label="Fermer"
                            type="button"
                        >
                            <X size={18} />
                        </button>
                    </div>

                    <div className="smModal__body">
                        <div className="smSection">
                            <div className="smSection__title">
                                <Mail size={16} />
                                Import depuis la boite mail
                            </div>

                            {/*<div className="smImportBar">*/}
                            {/*    <div className="smImportInfo">*/}
                            {/*        Récupérer les piéces jointes PDF, DOC et DOCX des émails non lus.*/}
                            {/*    </div>*/}

                            {/*    <button*/}
                            {/*        className="smBtn smBtn--primary"*/}
                            {/*        onClick={handleImportMailAttachments}*/}
                            {/*        disabled={importLoading}*/}
                            {/*        type="button"*/}
                            {/*    >*/}
                            {/*        {importLoading*/}
                            {/*            ? "Import en cours..."*/}
                            {/*            : "Importer depuis la boite mail"}*/}
                            {/*    </button>*/}
                            {/*</div>*/}
                            <div className="smImportInfo">
                                <span>Dernière synchronisation :</span>
                                <strong>{formatSynchronizationDate(lastSynchronization)}</strong>
                            </div>

                            {mailImportResult && (
                                <div className="smImportSummary">
                                    <div>Emails non lus : {mailImportResult.totalUnreadEmails || 0}</div>
                                    <div>Emails traités : {mailImportResult.processedEmails || 0}</div>
                                    <div>Fichiers importés : {mailImportResult.importedCount || 0}</div>
                                    <div>Fichiers ignorés : {mailImportResult.ignoredCount || 0}</div>
                                </div>
                            )}

                            {mailImportResult?.errors?.length > 0 && (
                                <div className="smImportErrors">
                                    {mailImportResult.errors.map((errorItem, index) => (
                                        <div key={`${errorItem}-${index}`}>{errorItem}</div>
                                    ))}
                                </div>
                            )}

                            {mailImportResult?.files?.length > 0 && (
                                <div className="smTableWrap">
                                    <table className="smTable">
                                        <thead>
                                        <tr>
                                            <th>Fichier</th>
                                            <th>Expediteur</th>
                                            <th>Objet</th>
                                            <th>Date reception</th>
                                            <th>Chemin relatif</th>
                                        </tr>
                                        </thead>
                                        <tbody>
                                        {mailImportResult.files.map((fileItem, index) => (
                                            <tr key={`${fileItem.storedFileName}-${index}`}>
                                                <td className="smStrong">
                                                    {fileItem.originalFileName || "-"}
                                                </td>
                                                <td className="smMuted">{fileItem.from || "-"}</td>
                                                <td className="smMuted">{fileItem.subject || "-"}</td>
                                                <td className="smMuted smDateCell">
                                                    {formatDateTime(fileItem.receivedDate)}
                                                </td>
                                                <td className="smMuted">
                                                    {fileItem.relativePath || "-"}
                                                </td>
                                            </tr>
                                        ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {/* ── Filtres ── */}
                        </div>
                        <div className="smSection">
                            <div className="smSection__title">
                                <Search size={16} />
                                Filtres de recherche
                            </div>

                            <div className="smGrid">
                                <div className="smField">
                                    <label>Nom Complet</label>
                                    <input
                                        type="text"
                                        value={filters.fullname}
                                        onChange={(e) =>
                                            setFilters({ ...filters, fullname: e.target.value })
                                        }
                                        placeholder="Rechercher..."
                                    />
                                </div>

                                <div className="smField">
                                    <label>Source</label>
                                    <select
                                        value={filters.source}
                                        onChange={(e) =>
                                            setFilters({ ...filters, source: e.target.value })
                                        }
                                    >
                                        <option value="">Toutes</option>
                                        {sourceOptions.map((s) => (
                                            <option key={s} value={s}>
                                                {s}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="smField">
                                    <label>Type de candidature</label>
                                    <select
                                        value={filters.typeCandidature}
                                        onChange={(e) =>
                                            setFilters({ ...filters, typeCandidature: e.target.value })
                                        }
                                    >
                                        <option value="">Tous</option>
                                        {typeCandidatureOptions.map((t) => (
                                            <option key={t} value={t}>
                                                {t}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="smField">
                                    <label>Expérience</label>
                                    <select
                                        value={filters.experience}
                                        onChange={(e) =>
                                            setFilters({ ...filters, experience: e.target.value })
                                        }
                                    >
                                        <option value="">Toutes</option>
                                        {experienceOptions.map((exp) => (
                                            <option key={exp} value={exp}>
                                                {exp}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="smField">
                                    <label>Diplôme</label>
                                    <input
                                        type="text"
                                        value={filters.diplomeObtenu}
                                        onChange={(e) =>
                                            setFilters({
                                                ...filters,
                                                diplomeObtenu: e.target.value,
                                            })
                                        }
                                        placeholder="Ex: Master..."
                                    />
                                </div>

                                <div className="smField">
                                    <label>Niveau d'étude</label>
                                    <select
                                        value={filters.niveauEtude}
                                        onChange={(e) =>
                                            setFilters({ ...filters, niveauEtude: e.target.value })
                                        }
                                    >
                                        <option value="">Tous</option>
                                        {niveauOptions.map((n) => (
                                            <option key={n} value={n}>
                                                {n}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="smField">
                                    <label>Sauvegarde</label>
                                    <select
                                        value={filters.sauvegarde}
                                        onChange={(e) =>
                                            setFilters({ ...filters, sauvegarde: e.target.value })
                                        }
                                    >
                                        <option value="">Tous</option>
                                        <option value="saved">Sauvegardés</option>
                                        <option value="unsaved">Non sauvegardés</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* ── Recommandations IA ── */}
                        {aiRecommendations.length > 0 && (
                            <div className="smSection">
                                <div className="smSection__title">
                                    <TrendingUp size={16} />
                                    Recommandations IA (Top 3)
                                </div>

                                <div className="smRecGrid">
                                    {aiRecommendations.map((candidat) => {
                                        const isSelected = selectedCandidates.some(
                                            (c) => Number(c.id) === Number(candidat.id)
                                        );
                                        const alreadyLinked = alreadyLinkedIds.has(
                                            Number(candidat.id)
                                        );
                                        const isSaved = savedIds.has(Number(candidat.id));
                                        const histCount = getHistorique(candidat.id).length;

                                        return (
                                            <div
                                                key={candidat.id}
                                                className="smRecCard"
                                                style={{
                                                    borderLeftColor: getScoreColor(
                                                        candidat.matchScore
                                                    ),
                                                }}
                                            >
                                                <div className="smRecTop">
                                                    <div className="smRecName">
                                                        {candidat.nomCandidat}{" "}
                                                        {candidat.prenomCandidat}
                                                    </div>
                                                    <div
                                                        style={{
                                                            display: "flex",
                                                            alignItems: "center",
                                                            gap: 6,
                                                        }}
                                                    >
                                                        {/* ── icône historique ── */}
                                                        <HistoriqueBtn
                                                            count={histCount}
                                                            onClick={() =>
                                                                setHistoriqueModal({
                                                                    open: true,
                                                                    candidat,
                                                                })
                                                            }
                                                        />
                                                        <div
                                                            className="smScore"
                                                            style={{
                                                                background: getScoreColor(
                                                                    candidat.matchScore
                                                                ),
                                                            }}
                                                        >
                                                            {candidat.matchScore}%
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="smRecMeta">
                                                    <div>📖 {candidat.diplomeObtenu || "—"}</div>
                                                    <div>
                                                        🎓 {labelNiveauEtude(candidat.niveauEtude)}
                                                    </div>
                                                    <div>💼 {candidat.anneExperience || "—"}</div>
                                                    <div>
                                                        🏛️ {getEtablissementLabel(candidat)}
                                                    </div>
                                                    <div>
                                                        📄{" "}
                                                        {labelFromOptions(
                                                            TYPE_CANDIDATURE_OPTIONS,
                                                            candidat.typeCandidature
                                                        )}
                                                    </div>
                                                    <div>
                                                        📌{" "}
                                                        {labelFromOptions(
                                                            SOURCE_OPTIONS,
                                                            candidat.source
                                                        )}
                                                    </div>
                                                    <div>📧 {candidat.emailCandidat || "—"}</div>
                                                </div>

                                                <div className="smRecActions">
                                                    <button
                                                        className={`smBtn ${
                                                            isSaved
                                                                ? "smBtn--muted"
                                                                : "smBtn--ghost"
                                                        }`}
                                                        onClick={() => toggleSaved(candidat)}
                                                        type="button"
                                                        title={
                                                            isSaved
                                                                ? "Retirer de la sauvegarde"
                                                                : "Sauvegarder"
                                                        }
                                                        disabled={alreadyLinked}
                                                    >
                                                        <Bookmark size={14} />
                                                        {alreadyLinked
                                                            ? "Déjà proposé"
                                                            : isSaved
                                                                ? "Sauvegardé"
                                                                : "Sauvegarder"}
                                                    </button>

                                                    <button
                                                        className={`smBtn ${
                                                            alreadyLinked
                                                                ? "smBtn--muted"
                                                                : isSelected
                                                                    ? "smBtn--muted"
                                                                    : "smBtn--primary"
                                                        }`}
                                                        onClick={() => toggleSelection(candidat)}
                                                        type="button"
                                                        disabled={alreadyLinked}
                                                    >
                                                        {alreadyLinked
                                                            ? "Déjà proposé"
                                                            : isSelected
                                                                ? "✓ Sélectionné"
                                                                : "Sélectionner"}
                                                    </button>

                                                    <button
                                                        className="smBtn smBtn--cv"
                                                        onClick={() => handleDownloadCV(candidat)}
                                                        type="button"
                                                        title={`Télécharger le CV de ${candidat.prenomCandidat}`}
                                                    >
                                                        <Download size={14} />
                                                        CV
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>

                                <div className="smMatchReport">
                                    <div className="smMatchReport__title">
                                        Rapport de matching
                                    </div>
                                    <div className="smTableWrap">
                                        <table className="smTable smMatchReport__table">
                                            <thead>
                                            <tr>
                                                <th>Nom complet</th>
                                                <th>Score</th>
                                                <th>Points forts</th>
                                                <th>Points de vigilance</th>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {aiRecommendations.map((candidat, index) => {
                                                const insights =
                                                    MATCHING_REPORT_INSIGHTS[index] ||
                                                    MATCHING_REPORT_INSIGHTS[2];

                                                return (
                                                    <tr key={`matching-report-${candidat.id}`}>
                                                        <td className="smStrong">
                                                            {candidat.nomCandidat} {candidat.prenomCandidat}
                                                        </td>
                                                        <td>
                                                            <span
                                                                className="smScore"
                                                                style={{
                                                                    background: getScoreColor(candidat.matchScore),
                                                                }}
                                                            >
                                                                {candidat.matchScore}%
                                                            </span>
                                                        </td>
                                                        <td className="smMuted">
                                                            {insights.strengths}
                                                        </td>
                                                        <td className="smMuted">
                                                            {insights.vigilance}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* ── Tableau tous les candidats ── */}
                        <div className="smSection">
                            <div className="smSection__title">
                                <User size={16} />
                                Tous les candidats ({filteredCandidats.length})
                            </div>

                            <div className="smTableWrap">
                                <table className="smTable">
                                    <thead>
                                    <tr>
                                        <th style={{ width: 40 }}>
                                            <input type="checkbox" disabled />
                                        </th>
                                        <th style={{ width: 56 }}>CV</th>
                                        <th style={{ width: 56 }}>Sauvegarde</th>
                                        <th style={{ width: 70 }}>Historique</th>
                                        <th>Nom complet</th>
                                        <th>Coordonnées</th>
                                        <th>Expérience</th>
                                        <th>Candidature</th>
                                        <th>Source</th>
                                        <th>Date</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {filteredCandidats.length === 0 ? (
                                        <tr>
                                            <td colSpan={10} className="smEmptyCell">
                                                Aucun candidat trouvé
                                            </td>
                                        </tr>
                                    ) : (
                                        filteredCandidats.map((candidat) => {
                                            const isSelected = selectedCandidates.some(
                                                (c) => Number(c.id) === Number(candidat.id)
                                            );
                                            const alreadyLinked = alreadyLinkedIds.has(
                                                Number(candidat.id)
                                            );
                                            const isSaved = savedIds.has(Number(candidat.id));
                                            const histCount = getHistorique(
                                                candidat.id
                                            ).length;

                                            return (
                                                <tr
                                                    key={candidat.id}
                                                    className={
                                                        isSelected ? "smRow--selected" : ""
                                                    }
                                                >
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() =>
                                                                toggleSelection(candidat)
                                                            }
                                                            disabled={alreadyLinked}
                                                        />
                                                    </td>

                                                    <td>
                                                        <button
                                                            className="smCvBtn"
                                                            onClick={() =>
                                                                handleDownloadCV(candidat)
                                                            }
                                                            title={`Télécharger ${
                                                                candidat.cv || "CV"
                                                            }`}
                                                            type="button"
                                                        >
                                                            <Download size={15} />
                                                        </button>
                                                    </td>

                                                    <td>
                                                        <button
                                                            className={`smCvBtn ${
                                                                isSaved ? "smCvBtn--saved" : ""
                                                            }`}
                                                            onClick={() =>
                                                                toggleSaved(candidat)
                                                            }
                                                            title={
                                                                isSaved
                                                                    ? "Retirer de la sauvegarde"
                                                                    : "Sauvegarder"
                                                            }
                                                            type="button"
                                                            disabled={alreadyLinked}
                                                        >
                                                            <Bookmark
                                                                size={15}
                                                                fill={
                                                                    isSaved
                                                                        ? "currentColor"
                                                                        : "none"
                                                                }
                                                            />
                                                        </button>
                                                    </td>

                                                    {/* ── icône historique ── */}
                                                    <td>
                                                        <HistoriqueBtn
                                                            count={histCount}
                                                            onClick={() =>
                                                                setHistoriqueModal({
                                                                    open: true,
                                                                    candidat,
                                                                })
                                                            }
                                                        />
                                                    </td>

                                                    <td className="smStrong">
                                                        {candidat.nomCandidat}{" "}
                                                        {candidat.prenomCandidat}
                                                        {alreadyLinked && (
                                                            <div
                                                                className="smMuted"
                                                                style={{ fontSize: 12 }}
                                                            >
                                                                Déjà proposé à cette demande
                                                            </div>
                                                        )}
                                                        {!alreadyLinked && isSaved && (
                                                            <div
                                                                className="smMuted"
                                                                style={{ fontSize: 12 }}
                                                            >
                                                                Candidat sauvegardé
                                                            </div>
                                                        )}
                                                    </td>

                                                    <td className="smMuted">
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                flexDirection: "column",
                                                                gap: 4,
                                                            }}
                                                        >
                                                            <div
                                                                style={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    gap: 6,
                                                                }}
                                                            >
                                                                <Mail size={13} />
                                                                <span>
                                                                        {candidat.emailCandidat ||
                                                                            "—"}
                                                                    </span>
                                                            </div>
                                                            <div
                                                                style={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    gap: 6,
                                                                }}
                                                            >
                                                                <Phone size={13} />
                                                                <span>
                                                                        {candidat.phoneCandidat ||
                                                                            "—"}
                                                                    </span>
                                                            </div>
                                                        </div>
                                                    </td>

                                                    <td className="smMuted">
                                                        {candidat.anneExperience || "—"}
                                                    </td>

                                                    <td className="smMuted">
                                                        {labelFromOptions(
                                                            TYPE_CANDIDATURE_OPTIONS,
                                                            candidat.typeCandidature
                                                        )}
                                                    </td>

                                                    <td className="smMuted">
                                                        {labelFromOptions(
                                                            SOURCE_OPTIONS,
                                                            candidat.source
                                                        )}
                                                    </td>

                                                    <td className="smMuted smDateCell">
                                                        {formatDate(candidat.dateAjout)}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                    <div className="smModal__footer">
                        <div className="smFooterLeft">
                            {selectedCandidates.length} candidat(s) sélectionné(s)
                        </div>

                        <button
                            className="smBtn smBtn--ghost"
                            onClick={onClose}
                            type="button"
                        >
                            Annuler
                        </button>

                        <button
                            className="smBtn smBtn--primary"
                            onClick={handleSave}
                            disabled={selectedCandidates.length === 0}
                            type="button"
                        >
                            Sélectionner
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Modal Historique / Tracking ── */}
            <HistoriqueModal
                open={historiqueModal.open}
                onClose={() => setHistoriqueModal({ open: false, candidat: null })}
                candidat={historiqueModal.candidat}
                historique={
                    historiqueModal.candidat
                        ? getHistorique(historiqueModal.candidat.id)
                        : []
                }
            />
        </>
    );
}
